import {ChildDataTypes} from '@stripe-internal/stripethirdparty';
import {MethodPayload} from '@stripe-internal/stripethirdparty/lib/shared/TwilioTypes';
import {ConversationParticipant} from '@stripe-internal/conversations-client';

export type STPTwilioInputMessage = ChildDataTypes['Twilio']['input'];
export type STPTwilioOutputMessage = ChildDataTypes['Twilio']['output'];

export type STPTwilioRequest = (
  message: STPTwilioInputMessage,
) => Promise<STPTwilioOutputMessage>;

export interface STPTwilioService {
  init: (token: string) => Promise<STPTwilioOutputMessage>;

  getUser: (id: string) => Promise<STPTwilioOutputMessage>;
  getMember: (id: string) => Promise<STPTwilioOutputMessage>;

  setActiveChannel: (id: string) => Promise<STPTwilioOutputMessage>;
  clearActiveChannel: (
    message: STPTwilioInputMessage,
  ) => Promise<STPTwilioOutputMessage>;
  leaveChannel: (
    participant: ConversationParticipant,
  ) => Promise<STPTwilioOutputMessage>;

  getMessages: (
    indexOffset?: number,
    direction?: 'forward' | 'backwards',
  ) => Promise<STPTwilioOutputMessage>;

  sendMessage: (
    text: string,
    nonce?: string,
  ) => Promise<STPTwilioOutputMessage>;
  sendFile: (file: File, nonce?: string) => Promise<STPTwilioOutputMessage>;
  sendTyping: () => Promise<STPTwilioOutputMessage>;
  sendUserEndedChat: () => Promise<STPTwilioOutputMessage>;

  shutdown: () => Promise<STPTwilioOutputMessage>;
}

const err = (
  method: MethodPayload['method'],
  error: string,
): Promise<STPTwilioOutputMessage> => {
  return Promise.resolve({type: 'method', method, error});
};

const notMounted = (method: MethodPayload['method']) =>
  err(method, 'stripethirdparty not mounted');

export const STPUnmounted: STPTwilioService = {
  init: () => notMounted('initialize'),
  getUser: () => notMounted('getUserDescriptor'),
  getMember: () => notMounted('getMember'),
  setActiveChannel: () => notMounted('setActiveChannel'),
  clearActiveChannel: () => notMounted('clearActiveChannel'),
  leaveChannel: () => notMounted('clearActiveChannel'),
  getMessages: () => notMounted('getMessages'),
  sendMessage: () => notMounted('sendMessage'),
  sendFile: () => notMounted('sendFile'),
  sendTyping: () => notMounted('sendTyping'),
  sendUserEndedChat: () => notMounted('sendUserEndedChatMessage'),
  shutdown: () => notMounted('shutdown'),
};

export class STPTwilio implements STPTwilioService {
  private stpSendRequest: STPTwilioRequest;

  constructor(stpSendRequest: STPTwilioRequest) {
    this.stpSendRequest = stpSendRequest;
  }

  init = async (token: string): Promise<STPTwilioOutputMessage> => {
    return this.send({
      method: 'initialize',
      options: {
        token,
      },
    });
  };

  getMember = async (
    participantId: string,
  ): Promise<STPTwilioOutputMessage> => {
    return this.send({
      method: 'getMember',
      options: {identity: participantId},
    });
  };

  getUser = async (participantId: string): Promise<STPTwilioOutputMessage> => {
    return this.send({
      method: 'getUserDescriptor',
      options: {identity: participantId},
    });
  };

  getMessages = async (
    indexOffset?: number,
    direction?: 'forward' | 'backwards',
  ): Promise<STPTwilioOutputMessage> => {
    return this.send({
      method: 'getMessages',
      options: {startingWith: indexOffset, direction},
    });
  };

  sendMessage = async (
    text: string,
    nonce?: string,
  ): Promise<STPTwilioOutputMessage> => {
    return this.send({
      method: 'sendMessage',
      options: {
        message: text,
        token: nonce,
      },
    });
  };

  sendFile = async (
    file: File,
    nonce?: string,
  ): Promise<STPTwilioOutputMessage> => {
    return this.send({
      method: 'sendFile',
      options: {
        file,
        token: nonce,
      },
    });
  };

  sendTyping = async (): Promise<STPTwilioOutputMessage> => {
    return this.send({method: 'sendTyping'});
  };

  leaveChannel = async (): Promise<STPTwilioOutputMessage> => {
    // our STP Twilio implementation doesn't actually ever "leave" the
    // channel. instead, it sends a custom "user left" message to the
    // channel and then clears the active channel, which essentially
    // is just removing the channel's event listeners within STP.

    const endChatResp = await this.sendUserEndedChat();
    if ('error' in endChatResp) {
      return endChatResp;
    }

    const clearResp = await this.clearActiveChannel();
    if ('error' in clearResp) {
      return clearResp;
    }

    return clearResp;
  };

  setActiveChannel = async (id: string): Promise<STPTwilioOutputMessage> => {
    const resp = await this.send({
      method: 'setActiveChannel',
      options: {
        sid: id,
      },
    });

    return resp;
  };

  sendUserEndedChat = async (): Promise<STPTwilioOutputMessage> => {
    return this.send({method: 'sendUserEndedChatMessage'});
  };

  clearActiveChannel = async (): Promise<STPTwilioOutputMessage> => {
    return this.send({method: 'clearActiveChannel'});
  };

  shutdown = async (): Promise<STPTwilioOutputMessage> => {
    return this.send({method: 'shutdown'});
  };

  private send = async (
    message: STPTwilioInputMessage,
  ): Promise<STPTwilioOutputMessage> => {
    return this.stpSendRequest(message);
  };
}
